import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { stjerneStreng, terningerTilStjerner } from '@/systems/anmeldelser';
import type { TerningTall } from '@/data/kritikere';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { sfx } from '@/systems/sfx';

const SHEET_WIDTH = 1000;
const GULL = 0xb8860b;
const OKER = 0xd9a14b;

/** Én kritikers dom slik modalen trenger den. */
export interface DomKritiker {
  kritikerNavn: string;
  terninger: TerningTall;
  sitat: string;
}

export interface DomDagData {
  tittel: string;
  forfatterNavn: string;
  dommer: DomKritiker[];
}

/**
 * Hvor lang pause mellom hver kritiker avsløres. Dette er selve
 * dramaturgien: spilleren venter på neste dom.
 */
const AVSLOR_INTERVALL_MS = 850;

/**
 * Aksent-farge per terningkast — gull for topp, dempet grått for slakt.
 */
function aksentForTerninger(t: TerningTall): number {
  const stjerner = terningerTilStjerner(t);
  if (stjerner >= 5) return GULL;
  if (stjerner === 4) return COLORS.forest;
  if (stjerner === 3) return OKER;
  return 0x9a8c7a;
}

/**
 * DomDagModal — «Anmelderdagen». De fire kritikerne avsløres etter tur med
 * terningkast og sitat, og til slutt slås snittet fast. Erstatter de fire
 * stille toastene med ett iscenesatt moment of truth, à la Mad TVs 20:00.
 */
export class DomDagModal {
  private layer?: Phaser.GameObjects.Container;
  private timers: Phaser.Time.TimerEvent[] = [];
  private onClose?: () => void;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(data: DomDagData, onClose?: () => void): void {
    this.close();
    this.onClose = onClose;

    const redusert = foretrekkerRedusertBevegelse();

    this.layer = this.scene.add.container(0, 0).setDepth(200);
    animateModalGlide(this.scene, this.layer);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 150;
    const innholdsBredde = SHEET_WIDTH - 64;

    // Mål opp innholdshøyden før vi tegner arket, så det blir kompakt.
    const radHoyder = data.dommer.map((d) => this.maalRadHoyde(d, innholdsBredde));
    let innhold = 0;
    innhold += 150; // header (overskrift + tittel + forfatter)
    for (const h of radHoyder) innhold += h + 14;
    innhold += 120; // snitt-celle
    innhold += 110; // knapp-sone
    const sheetH = Math.min(GAME_HEIGHT - 240, innhold);

    const sheet = lagRundetRekt(
      this.scene,
      sheetX,
      sheetY,
      SHEET_WIDTH,
      sheetH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(4, COLORS.wine, 1);
    this.layer.add(sheet);

    const topBorder = lagRundetRekt(this.scene, sheetX, sheetY, SHEET_WIDTH, 12, COLORS.wine, 1, 4);
    this.layer.add(topBorder);

    let y = sheetY + 36;
    const venstre = sheetX + 32;

    const kicker = this.scene.add
      .text(venstre, y, 'ANMELDERDAGEN', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    this.layer.add(kicker);
    y += 30;

    const tittel = this.scene.add
      .text(venstre, y, `«${data.tittel}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#862736',
        wordWrap: { width: innholdsBredde },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);
    y += tittel.height + 6;

    const forfatter = this.scene.add
      .text(venstre, y, `av ${data.forfatterNavn} — dommen er falt`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    this.layer.add(forfatter);
    y += 44;

    // Kritiker-rader: bygg dem skjult, avslør etter tur.
    const radContainere: Phaser.GameObjects.Container[] = [];
    data.dommer.forEach((d, i) => {
      const h = radHoyder[i];
      const rad = this.byggRad(d, venstre, y, innholdsBredde, h);
      rad.setAlpha(0);
      if (!redusert) rad.y += 12;
      this.layer?.add(rad);
      radContainere.push(rad);
      y += h + 14;
    });

    // Snitt-celle: bygges skjult, avsløres etter siste kritiker.
    const snittCelle = this.byggSnittCelle(data.dommer, venstre, y, innholdsBredde);
    snittCelle.setAlpha(0);
    this.layer.add(snittCelle);

    // Lukk-knapp (synlig fra start, så ingen føler seg fanget).
    const knappH = 64;
    const knappW = 280;
    const knappX = sheetX + (SHEET_WIDTH - knappW) / 2;
    const knappY = sheetY + sheetH - knappH - 28;
    const knappBg = lagRundetRekt(this.scene, knappX, knappY, knappW, knappH, COLORS.wine, 1, 8);
    this.layer.add(knappBg);
    const knappTekst = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Fortsett  →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add(knappTekst);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
    knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
    knappBg.on('pointerup', () => this.close());

    // ---- Avsløringssekvens ----
    radContainere.forEach((rad, i) => {
      this.planlegg(i * AVSLOR_INTERVALL_MS, () => {
        sfx.play('pageturn');
        if (redusert) {
          rad.setAlpha(1);
        } else {
          this.scene.tweens.add({
            targets: rad,
            alpha: 1,
            y: rad.y - 12,
            duration: 320,
            ease: 'Back.easeOut',
          });
        }
      });
    });

    const snittDelay = radContainere.length * AVSLOR_INTERVALL_MS + 300;
    this.planlegg(snittDelay, () => {
      const snitt = this.snittTerninger(data.dommer);
      if (snitt >= 4.5) {
        sfx.play('fanfare');
        this.kjorKonfetti();
      } else if (snitt >= 3.5) {
        sfx.play('bell');
      } else {
        sfx.play('notify');
      }
      if (redusert) {
        snittCelle.setAlpha(1);
      } else {
        this.scene.tweens.add({
          targets: snittCelle,
          alpha: 1,
          duration: 360,
          ease: 'Sine.easeOut',
        });
      }
    });
  }

  close(): void {
    for (const t of this.timers) t.remove(false);
    this.timers = [];
    this.layer?.destroy(true);
    this.layer = undefined;
    const cb = this.onClose;
    this.onClose = undefined;
    cb?.();
  }

  // ---------- Bygg ----------

  private planlegg(delayMs: number, fn: () => void): void {
    const t = this.scene.time.delayedCall(delayMs, fn);
    this.timers.push(t);
  }

  private maalRadHoyde(d: DomKritiker, bredde: number): number {
    const proev = this.scene.add
      .text(0, 0, d.sitat, {
        fontFamily: FONTS.body,
        fontSize: fontStr(17),
        fontStyle: 'italic',
        wordWrap: { width: bredde - 56 },
        lineSpacing: 3,
      })
      .setOrigin(0, 0);
    const sitatH = proev.height;
    proev.destroy();
    return Math.max(96, 54 + sitatH + 22);
  }

  private byggRad(
    d: DomKritiker,
    x: number,
    y: number,
    bredde: number,
    hoyde: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const aksent = aksentForTerninger(d.terninger);

    const bg = this.scene.add
      .rectangle(x, y, bredde, hoyde, 0xf3ece0, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.18);
    const stripe = this.scene.add.rectangle(x, y, 6, hoyde, aksent, 1).setOrigin(0, 0);
    c.add(bg);
    c.add(stripe);

    const navn = this.scene.add
      .text(x + 28, y + 18, d.kritikerNavn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    c.add(navn);

    const stjerner = this.scene.add
      .text(x + bredde - 24, y + 18, stjerneStreng(d.terninger), {
        fontFamily: FONTS.display,
        fontSize: fontStr(26),
        color: Phaser.Display.Color.IntegerToColor(aksent).rgba,
      })
      .setOrigin(1, 0);
    c.add(stjerner);

    const sitat = this.scene.add
      .text(x + 28, y + 54, `«${d.sitat}»`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(17),
        color: '#3a2e22',
        fontStyle: 'italic',
        wordWrap: { width: bredde - 56 },
        lineSpacing: 3,
      })
      .setOrigin(0, 0);
    c.add(sitat);

    return c;
  }

  private byggSnittCelle(
    dommer: DomKritiker[],
    x: number,
    y: number,
    bredde: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(0, 0);
    const snitt = this.snittTerninger(dommer);
    const stjerner = Math.max(1, Math.round((snitt * 5) / 6));
    const aksent =
      stjerner >= 5 ? GULL : stjerner === 4 ? COLORS.forest : stjerner === 3 ? OKER : 0x9a8c7a;

    const h = 100;
    const bg = lagRundetRekt(this.scene, x, y, bredde, h, COLORS.beige, 1, 10).setStrokeStyle(
      2,
      aksent,
      0.9,
    );
    c.add(bg);

    const label = this.scene.add
      .text(x + 24, y + 22, 'SAMLET DOM', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    c.add(label);

    const verdi = this.scene.add
      .text(x + 24, y + 46, `Snitt ${snitt.toFixed(1)} / 6`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    c.add(verdi);

    const stjernerTxt = this.scene.add
      .text(x + bredde - 24, y + h / 2, '★'.repeat(stjerner) + '☆'.repeat(5 - stjerner), {
        fontFamily: FONTS.display,
        fontSize: fontStr(34),
        color: Phaser.Display.Color.IntegerToColor(aksent).rgba,
      })
      .setOrigin(1, 0.5);
    c.add(stjernerTxt);

    return c;
  }

  private snittTerninger(dommer: DomKritiker[]): number {
    if (dommer.length === 0) return 0;
    let sum = 0;
    for (const d of dommer) sum += d.terninger;
    return sum / dommer.length;
  }

  private kjorKonfetti(): void {
    if (!this.layer) return;
    const farger = [COLORS.wine, GULL, OKER];
    const lag = this.scene.add.container(0, 0).setDepth(210);
    this.layer.add(lag);
    for (let i = 0; i < 28; i += 1) {
      const x = Math.random() * GAME_WIDTH;
      const farge = farger[Math.floor(Math.random() * farger.length)];
      const str = 6 + Math.floor(Math.random() * 8);
      const p = this.scene.add.rectangle(x, -20, str, str, farge, 1).setOrigin(0.5);
      p.setAngle(Math.random() * 360);
      lag.add(p);
      this.scene.tweens.add({
        targets: p,
        y: GAME_HEIGHT + 40,
        angle: p.angle + 360 * (Math.random() > 0.5 ? 1 : -1),
        alpha: 0,
        duration: 2500 + Math.random() * 1500,
        ease: 'Cubic.easeIn',
        delay: Math.random() * 500,
        onComplete: () => p.destroy(),
      });
    }
  }
}
